import { 
  ApiDataType,
  CardsInfosSectionsType, 
  FamilyContextType, 
  FamilyMemberType, 
  QuestionType 
} from "types/services/Family"

import { splitString } from "./utils"

interface MemberCardSectionsType {
  name: string
  description: string
  cardsInfos: QuestionType[]
}

export const handleAnswer = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answer: any,
  formatType: 'data' | 'money' | 'text' | 'boolean' = 'text'
) => {
  if (answer === undefined) return 'Sem informação'
  switch (formatType) {
    case 'money':
      return Number(answer).toLocaleString('pt-br', {
        style: 'currency',
        currency: 'BRL',
      })

    case 'data':
      return new Date(answer).toLocaleDateString('pt-br')

    case 'boolean':
      return answer === true ? 'Sim' : 'Não'

    default:
      return answer
  }
}

export function formatMemberDesc(member: FamilyMemberType): string {
  const kinship = member?.kinship ?? ''
  const role = member?.role ?? ''
  const gender = member?.gender ?? ''
  const age = member?.age ?? ''
  const color = member?.color ?? ''
  const maritalStatus = member?.maritalStatus ?? ''

  if (
    kinship.length === 0 &&
    role.length === 0 &&
    gender.length === 0 &&
    age.toString().length === 0 &&
    color.length === 0 &&
    maritalStatus.length === 0
  )
    return 'Sem informações'

  const kinshipInfo = `${kinship}, `
  const roleInfo = `${role}, `
  const genderInfo = `sexo ${gender.toLowerCase()}, `
  const ageInfo = `${age} ${age === 1 ? 'ano' : 'anos'}`
  const colorInfo = `, cor ou raça ${color.toLowerCase()}`
  const maritalStatusInfo = `, ${maritalStatus.toLowerCase()}`

  return (
    `${kinship !== '' ? kinshipInfo : ''}` +
    `${role !== '' ? roleInfo : ''}` +
    `${gender !== '' ? genderInfo : ''}` +
    `${age.toString() !== '' ? ageInfo : ''}` +
    `${color !== '' ? colorInfo : ''}` +
    `${maritalStatus !== '' ? maritalStatusInfo : ''}`
  )
}

export function getSummationLastMonthIncomePerMember(
  acumulation: number,
  currentValue: {
    workAndIncome: {
      lastMonthWorkIncome: number
      otherIncomesPerMonth: number
      retirementIncomesPerMonth: number
      donationIncomesPerMonth: number
      alimonyIncomesPerMonth: number
      unemploymentInsurranceIncomesPerMonth: number
    }
  }
) {
  const {
    lastMonthWorkIncome = 0 ?? 0,
    otherIncomesPerMonth = 0,
    retirementIncomesPerMonth = 0,
    donationIncomesPerMonth = 0,
    alimonyIncomesPerMonth = 0,
    unemploymentInsurranceIncomesPerMonth = 0,
  } = currentValue?.workAndIncome

  const total =
    lastMonthWorkIncome +
    otherIncomesPerMonth +
    retirementIncomesPerMonth +
    donationIncomesPerMonth +
    alimonyIncomesPerMonth +
    unemploymentInsurranceIncomesPerMonth

  return acumulation + total
}

// Calcula o número de alertas em cada seção
export function calculateNumberAlertsPerSections(
  cardInfoSections: CardsInfosSectionsType[]
) {
  let income = 0
  let education = 0
  let residence = 0
  let health = 0
  let pregnancy = 0
  let nutrition = 0

  cardInfoSections?.forEach((infoSection: CardsInfosSectionsType) => {
    const isEmptyObject = Object.keys(infoSection).includes('family')

    if (!isEmptyObject) {
      // income
      income =
        income +
        (infoSection?.income?.allMembers.cardsInfos.filter(
          (card: QuestionType) => card.isAlert === true
        ).length ?? 0)

      infoSection?.income?.members.forEach(
        (member: MemberCardSectionsType) =>
          (income =
            income +
            member.cardsInfos.filter(
              (card: QuestionType) => card.isAlert === true
            ).length)
      )

      // education
      infoSection?.education?.members.forEach(
        (member: MemberCardSectionsType) =>
          (education =
            education +
            member.cardsInfos.filter(
              (card: QuestionType) => card.isAlert === true
            ).length)
      )

      // health
      infoSection?.health?.members.forEach(
        (member: MemberCardSectionsType) =>
          (health =
            health +
            member.cardsInfos.filter((card: QuestionType) => card.isAlert)
              .length)
      )

      // residence
      residence =
        residence +
        (infoSection?.residence?.cardsInfos.filter(
          (card: QuestionType) => card.isAlert === true
        ).length ?? 0)

      // pregnancy
      pregnancy =
        pregnancy +
        (infoSection?.pregnancy?.cardsInfos.filter(
          (card: QuestionType) => card.isAlert === true
        ).length ?? 0)

      // nutrition
      nutrition =
        nutrition +
        (infoSection?.nutrition?.cardsInfos.filter(
          (card: QuestionType) => card.isAlert === true
        ).length ?? 0)
    }
  })

  return {
    income,
    education,
    residence,
    health,
    pregnancy,
    nutrition,
  }
}

// Pega as informações que estão em alerta, por base
export function getAlertsTheBase(
  cardInfoSections: CardsInfosSectionsType[],
  base: string
) {
  const baseCorrect = cardInfoSections.filter(
    (cardInfoSectionsTheBase: CardsInfosSectionsType) =>
      cardInfoSectionsTheBase.base === base
  )[0]
  const alerts = []

  const isEmptyObject = Object.keys(baseCorrect).includes('family')

  if (!isEmptyObject) {
    alerts.push(
      ...(baseCorrect.income?.allMembers.cardsInfos.map(
        (card: QuestionType) => {
          if (card.isAlert === true) {
            return {
              keySection: 'renda',
              question: 'Renda',
              answer: card.question,
              isAlert: card.isAlert,
              type: 'text',
            }
          }
          return null
        }
      ) ?? [])
    )

    baseCorrect.income?.members.forEach((member: MemberCardSectionsType) =>
      alerts.push(
        ...member.cardsInfos.map((card: QuestionType) => {
          if (card.isAlert === true) {
            return {
              keySection: 'renda',
              question: 'Renda',
              answer: card.question,
              isAlert: card.isAlert,
              type: 'text',
            }
          }
          return null
        })
      )
    )

    baseCorrect.education?.members.forEach((member: MemberCardSectionsType) =>
      alerts.push(
        ...member.cardsInfos.map((card: QuestionType) => {
          if (card.isAlert === true) {
            return {
              keySection: 'educacao',
              question: 'Educação',
              answer: card.question,
              isAlert: card.isAlert,
              type: 'text',
            }
          }
          return null
        })
      )
    )

    baseCorrect.health?.members.forEach((member: MemberCardSectionsType) =>
      alerts.push(
        ...(member.cardsInfos.map((card: QuestionType) => {
          if (card.isAlert === true) {
            return {
              keySection: 'saude',
              question: 'Saúde',
              answer: card.question,
              isAlert: card.isAlert,
              type: 'text',
            }
          }
          return null
        }) ?? [])
      )
    )

    alerts.push(
      ...(baseCorrect.residence?.cardsInfos.map((card: QuestionType) => {
        if (card.isAlert === true) {
          return {
            keySection: 'habitacao',
            question: 'Habitação',
            answer: card.question,
            isAlert: card.isAlert,
            type: 'text',
          }
        }
        return null
      }) ?? [])
    )

    alerts.push(
      ...(baseCorrect.pregnancy?.cardsInfos.map((card: QuestionType) => {
        if (card.isAlert === true) {
          return {
            keySection: 'gravidez',
            question: 'Gravidez',
            answer: card.question,
            isAlert: card.isAlert,
            type: 'text',
          }
        }
        return null
      }) ?? [])
    )
    alerts.push(
      ...(baseCorrect.nutrition?.cardsInfos.map((card: QuestionType) => {
        if (card.isAlert === true) {
          return {
            keySection: 'nutricao',
            question: 'Nutrição',
            answer: card.question,
            isAlert: card.isAlert,
            type: 'text',
          }
        }
        return null
      }) ?? [])
    )
  }

  return alerts.filter((alert) => alert) as unknown as Array<{
    keySection: string
    question: string
    answer: string
    isAlert: boolean
    type: 'boolean' | 'data' | 'text' | 'money'
  }>
}

export function getInfosMember(
  nameMember: string,
  base: string,
  families: FamilyContextType[]
) {
  let memberInfo: FamilyMemberType = {} as FamilyMemberType

  families.forEach((family) => {
    if (family.base === base) {
      if (family?.family?.familyHead?.name === nameMember) {
        memberInfo = family.family.familyHead
      } else {
        family?.family?.members.forEach((member) => {
          if (member.name === nameMember) {
            memberInfo = member
          }
        })
      }
    }
  })

  return memberInfo
}

export function getMemberFamilyHead(
  base: string,
  families: FamilyContextType[]
) {
  let memberFamilyHead: FamilyMemberType = {} as FamilyMemberType

  families?.forEach((family) => {
    if (family.base === base) {
      memberFamilyHead = family?.family?.familyHead ?? memberFamilyHead
    }
  })

  return memberFamilyHead
}

export function labelStringNutrition(fieldName: string) {
  const lastString = splitString(fieldName, '.').toLowerCase()
  const food = lastString.includes('food')
  const meal = lastString.includes('meal')
  const consumption = lastString.includes('consumption')

  if (food) return 'food'
  else if (meal) return 'meal'
  else if (consumption) return 'consumption'
  return 'null'
}

type YesOrNo = 'Sim' | 'Não'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getSomeoneWorks(members: any[]) {
  let yesOrNo: YesOrNo = 'Não'

  members.forEach((member) => {
    if (yesOrNo === 'Não' && member.workAndIncome?.workedLastWeek === true)
      yesOrNo = 'Sim'
  })

  return yesOrNo
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getNumberOfWorkers(family: any) {
  let numberOfWorkers =
    family?.familyHead?.workAndIncome?.awayFromWork === true ||
    family?.familyHead?.workAndIncome?.workedLastWeek === true
      ? 1
      : 0

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  family.members.forEach((member: any) => {
    if (
      member.workAndIncome?.awayFromWork === true ||
      member.workAndIncome?.workedLastWeek === true
    )
      numberOfWorkers += 1
  })

  return numberOfWorkers
}

export function getCheckInfosKinship(
  data: ApiDataType[],
  cpf: string
): FamilyMemberType {
  let infosCheck = data.find(
    (info: ApiDataType) => info?.family?.familyHead?.cpf === cpf
  )

  if (infosCheck === undefined) {
    /* caso nao encontre um com cpf igual ao da busca, entra aqui */
    infosCheck = data.find((info: ApiDataType) => info?.family?.familyHead?.cpf)
  }

  return infosCheck?.family.familyHead
}

export function getBasesQuerySort(basesQuery: string[], data: ApiDataType[]) {
  return basesQuery.sort((ant, pos) => {
    const antFamilyWithData: boolean =
      data.filter((family) => family?.base === ant)[0]?.family !== undefined
    const posFamilyWithData: boolean =
      data.filter((family) => family?.base === pos)[0]?.family !== undefined

    if (!antFamilyWithData && posFamilyWithData) return 1
    if (antFamilyWithData && !posFamilyWithData) return -1
    return 0
  })
}